import { useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Menu,
  X,
  Shield,
  BookOpen,
  Package,
  BarChart3,
  GraduationCap,
  User,
  Settings,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const navLinks = [
  { href: "/framework", label: "Framework", icon: Shield },
  { href: "/protocols", label: "Protocols", icon: BookOpen },
  { href: "/toolkits", label: "Toolkits", icon: Package },
  { href: "/data", label: "Data Guides", icon: BarChart3 },
  { href: "/training", label: "Training", icon: GraduationCap },
  { href: "/about", label: "About", icon: User },
];

export default function Layout() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <img 
                src="/favicon.svg" 
                alt="PPS PlayBook" 
                className="w-10 h-10 rounded-sm"
              />
              <div className="hidden sm:block">
                <span className="font-bold text-lg text-slate-900 tracking-tight">
                  PPS PlayBook
                </span>
                <span className="text-xs text-slate-500 block -mt-1">
                  Systems for Student Support
                </span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`nav-link px-4 py-2 text-sm font-medium transition-colors ${
                    isActive(link.href)
                      ? "text-slate-900"
                      : "text-slate-600 hover:text-slate-900"
                  }`}
                  data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Right side */}
            <div className="flex items-center gap-3">
              {/* Mobile menu button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 text-slate-600 hover:text-slate-900"
                data-testid="mobile-menu-toggle"
              >
                {mobileMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden bg-white border-t border-slate-200"
              data-testid="mobile-nav"
            >
              <nav className="px-4 py-4 space-y-1">
                {navLinks.map((link) => {
                  const Icon = link.icon;
                  return (
                    <Link
                      key={link.href}
                      to={link.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-md transition-colors ${
                        isActive(link.href)
                          ? "bg-slate-100 text-slate-900"
                          : "text-slate-600 hover:bg-slate-50"
                      }`}
                      data-testid={`mobile-nav-${link.label.toLowerCase().replace(' ', '-')}`}
                    >
                      <Icon className="w-5 h-5" strokeWidth={1.5} />
                      {link.label}
                    </Link>
                  );
                })}
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Brand */}
            <div className="md:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <img 
                  src="/favicon.svg" 
                  alt="PPS PlayBook" 
                  className="w-10 h-10 rounded-sm"
                />
                <span className="font-bold text-lg">PPS PlayBook</span>
              </div>
              <p className="text-slate-400 text-sm max-w-md">
                Building coherent, repeatable systems for student support. 
                Protocols, tools, and data guides for PPS leaders.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="label-uppercase text-slate-400 mb-4">Resources</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/protocols" className="text-slate-300 hover:text-white text-sm">
                    Protocol Library
                  </Link>
                </li>
                <li>
                  <Link to="/toolkits" className="text-slate-300 hover:text-white text-sm">
                    Toolkits
                  </Link>
                </li>
                <li>
                  <Link to="/data" className="text-slate-300 hover:text-white text-sm">
                    Data Guides
                  </Link>
                </li>
                <li>
                  <Link to="/training" className="text-slate-300 hover:text-white text-sm">
                    Training
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="label-uppercase text-slate-400 mb-4">Connect</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/about" className="text-slate-300 hover:text-white text-sm">
                    About
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-slate-500 text-sm">
            <p>&copy; {new Date().getFullYear()} PPS PlayBook All rights reserved.</p>
            <p className="mt-2 text-xs max-w-2xl mx-auto">
              This site provides leadership frameworks and operational guidance and is not legal or clinical advice.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
